<template>
  <div :class="DS_SLIDER_INPUT_CLASSES.Slider">
    <input
      :id="props.id"
      ref="rangeInputRef"
      :class="DS_SLIDER_INPUT_CLASSES.Input"
      data-cy="ds-slider-input__input"
      type="range"
      :name="props.name"
      :value="props.modelValue"
      :min="props.min"
      :max="props.max"
      :step="props.step"
      :disabled="props.disabled"
      :required="props.required"
      :aria-label="props.accessibleLabel"
      :aria-invalid="props.hasError ? 'true' : undefined"
      :aria-describedby="props.describedBy"
      @input="onInput"
      @change="emit('change', $event)"
      @focus="emit('focus', $event)"
      @blur="emit('blur', $event)"
    />

    <span
      v-if="props.showTickMarks && props.tickMarkStyles.length"
      :class="DS_SLIDER_INPUT_CLASSES.TickMarks"
      data-cy="ds-slider-input__tick-marks"
      aria-hidden="true"
    >
      <span
        v-for="(tickStyle, index) in props.tickMarkStyles"
        :key="index"
        :class="DS_SLIDER_INPUT_CLASSES.Tick"
        :style="tickStyle"
        data-cy="ds-slider-input__tick"
      />
    </span>

    <span
      v-if="props.showThumbIcon"
      :class="DS_SLIDER_INPUT_CLASSES.ThumbIconTrack"
      aria-hidden="true"
    >
      <DSIcon
        :class="DS_SLIDER_INPUT_CLASSES.ThumbIcon"
        icon="dragger"
        size="sm"
        data-cy="ds-slider-input__thumb-icon"
      />
    </span>

    <!-- Integrate DSTooltip here when the reusable primitive is ready. -->
  </div>
</template>

<script setup lang="ts">
import { ref, type CSSProperties } from "vue";
import type { DSSliderInputEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_SLIDER_INPUT_CLASSES } from "@/src/design_system/form_elements/slider_input/constants/classes";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";

interface DSSliderInputProps {
  id: string;
  name: string;
  modelValue: number;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  required: boolean;
  accessibleLabel?: string;
  hasError: boolean;
  describedBy?: string;
  showTickMarks: boolean;
  tickMarkStyles: CSSProperties[];
  showThumbIcon: boolean;
}

const props = defineProps<DSSliderInputProps>();
const emit = defineEmits<DSSliderInputEmits>();

const rangeInputRef = ref<HTMLInputElement>();
const { focus, blur } = useDSInput(rangeInputRef);

function onInput(event: Event) {
  emit("update:modelValue", (event.target as HTMLInputElement).valueAsNumber);
}

defineExpose({
  focus,
  blur,
  isFocused: () => document.activeElement === rangeInputRef.value,
});
</script>
